import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import {
  AuditActor,
  AuditEntry,
  AuditSearchCriteria,
  ConnectionMetrics,
  UserActivitySummary,
  UserSession,
  UserSessionCriteria,
} from '../../interfaces/activity';
import { ApiPaginatedResponse } from '../../interfaces/question-search';
import {
  ApiCollectionResponse,
  ApiSingleResponse,
} from '../../interfaces/responses/api-response';

/**
 * Journal d'activité et fréquentation, côté administration.
 *
 * <p>Lecture seule : le serveur n'expose aucune écriture, et c'est ce qui fait
 * la valeur du journal — une ligne qu'on pourrait effacer ne prouve rien.</p>
 */
@Injectable({ providedIn: 'root' })
export class AdminActivityService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.BASE_URL}/admin/activity`;

  searchAudit(
    criteria: AuditSearchCriteria,
  ): Observable<ApiPaginatedResponse<AuditEntry>> {
    return this.http.get<ApiPaginatedResponse<AuditEntry>>(`${this.base}/audit`, {
      params: toParams(criteria),
    });
  }

  /**
   * Mon journal — celui de mes seules actions.
   *
   * <p>Un endpoint distinct, et non un filtre passé à `searchAudit` : le
   * périmètre est **imposé par le serveur** à partir du jeton. Un filtre que
   * l'appelant fournit, il peut le retirer ; ce ne serait pas un périmètre,
   * seulement une suggestion.</p>
   *
   * <p>Ouvert par `activity.read.own`, que porte tout compte — là où le journal
   * complet demande `activity.read.all`.</p>
   */
  searchMyAudit(
    criteria: AuditSearchCriteria,
  ): Observable<ApiPaginatedResponse<AuditEntry>> {
    return this.http.get<ApiPaginatedResponse<AuditEntry>>(
      `${environment.BASE_URL}/me/activity`,
      { params: toParams(criteria) },
    );
  }

  auditActors(): Observable<ApiCollectionResponse<AuditActor>> {
    return this.http.get<ApiCollectionResponse<AuditActor>>(
      `${this.base}/audit/actors`,
    );
  }

  auditActions(): Observable<ApiCollectionResponse<string>> {
    return this.http.get<ApiCollectionResponse<string>>(`${this.base}/audit/actions`);
  }

  /** Les actes de mon seul journal — la facette que `activity.read.own` ouvre. */
  myAuditActions(): Observable<ApiCollectionResponse<string>> {
    return this.http.get<ApiCollectionResponse<string>>(
      `${environment.BASE_URL}/me/activity/actions`,
    );
  }

  searchSessions(
    criteria: UserSessionCriteria,
  ): Observable<ApiPaginatedResponse<UserSession>> {
    return this.http.get<ApiPaginatedResponse<UserSession>>(`${this.base}/sessions`, {
      params: toParams(criteria),
    });
  }

  /** Fréquentation sur la période ; sans bornes, le serveur retient 30 jours. */
  metrics(from?: string, to?: string): Observable<ApiSingleResponse<ConnectionMetrics>> {
    return this.http.get<ApiSingleResponse<ConnectionMetrics>>(
      `${this.base}/sessions/metrics`,
      { params: toParams({ from, to }) },
    );
  }

  userActivity(role?: string): Observable<ApiCollectionResponse<UserActivitySummary>> {
    return this.http.get<ApiCollectionResponse<UserActivitySummary>>(
      `${this.base}/sessions/by-user`,
      { params: toParams({ role }) },
    );
  }
}

/**
 * Construit les paramètres en écartant les valeurs vides.
 *
 * <p>Un `actorRole=` transmis tel quel transformerait un filtre absent en
 * filtre sur la chaîne vide, et la liste reviendrait désespérément vide.</p>
 */
function toParams(criteria: object): HttpParams {
  let params = new HttpParams();
  for (const [key, value] of Object.entries(criteria)) {
    if (value === null || value === undefined || value === '') continue;
    params = params.set(key, String(value));
  }
  return params;
}
